import { createTheme } from "@mui/material/styles";

/** MUI theme for @mui/x-date-pickers aligned with app shadcn tokens (student schedule + lesson dialogs). */
export function createScheduleTimeMuiTheme(mode: "light" | "dark") {
  return createTheme({
    palette: {
      mode,
      primary: { main: "hsl(217 91% 60%)" },
    },
    typography: { fontFamily: "inherit" },
    shape: { borderRadius: 8 },
    components: {
      MuiPickersTextField: {
        styleOverrides: {
          root: { marginTop: 0, marginBottom: 0 },
        },
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "var(--radius)",
            backgroundColor: "hsl(var(--background))",
            color: "hsl(var(--foreground))",
            minHeight: 36,
            // Match MUI PickersOutlinedInput default (`0 14px`); 0.25rem was too tight vs border/icon.
            paddingLeft: "14px",
            paddingRight: "14px",
            transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            "&:hover:not(.Mui-disabled) .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: "hsl(var(--input))",
            },
            "&.Mui-focused": {
              boxShadow:
                "0 0 0 2px hsl(var(--background)), 0 0 0 4px hsl(var(--ring))",
            },
            "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: "hsl(var(--ring))",
              borderWidth: "1px",
            },
          },
          notchedOutline: {
            borderColor: "hsl(var(--input))",
          },
          sectionsContainer: {
            padding: "8px 0",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: "hsl(var(--muted-foreground))",
            "&:hover": {
              backgroundColor: "hsl(var(--accent))",
              color: "hsl(var(--accent-foreground))",
            },
          },
        },
      },
      MuiPickerPopper: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: "hsl(var(--popover))",
            color: "hsl(var(--popover-foreground))",
            border: "1px solid hsl(var(--popover-border))",
            borderRadius: "var(--radius)",
            boxShadow:
              "0 10px 15px -3px rgb(0 0 0 / 0.12), 0 4px 6px -4px rgb(0 0 0 / 0.08)",
          },
        },
      },
      MuiPickersLayout: {
        styleOverrides: {
          root: {
            backgroundColor: "hsl(var(--popover))",
            color: "hsl(var(--popover-foreground))",
          },
        },
      },
      MuiTimeClock: {
        styleOverrides: {
          root: { color: "hsl(var(--popover-foreground))" },
        },
      },
      MuiClock: {
        styleOverrides: {
          pin: {
            backgroundColor: "hsl(var(--primary))",
            width: 8,
            height: 8,
          },
        },
      },
      // Do not override MuiClockPointer `thumb`: default uses a thick border ring (~16px) +
      // small center; a thin border override made the selection dot look broken.
      MuiClockPointer: {
        styleOverrides: {
          root: { backgroundColor: "hsl(var(--primary))" },
        },
      },
      MuiClockNumber: {
        styleOverrides: {
          root: {
            color: "hsl(var(--popover-foreground))",
            "&.MuiClockNumber-selected": {
              backgroundColor: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
            },
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          previousIconButton: {
            color: "hsl(var(--foreground))",
            "&:hover": { backgroundColor: "hsl(var(--accent))" },
          },
          nextIconButton: {
            color: "hsl(var(--foreground))",
            "&:hover": { backgroundColor: "hsl(var(--accent))" },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "8px 12px",
            backgroundColor: "hsl(var(--popover))",
            borderTop: "1px solid hsl(var(--border))",
            "& .MuiButton-text": {
              color: "hsl(var(--foreground))",
            },
            "& .MuiButton-containedPrimary": {
              backgroundColor: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "hsl(217 91% 52%)",
              },
            },
          },
        },
      },
    },
  });
}
